import { getCurrencySymbol } from "./formatters";
import { getCompanyCurrencyCodeUpper } from "./manualDocumentCurrency";

export function isReverseChargeVendor(
  gstTreatment?: string,
  gstCategory?: string,
): boolean {
  const treatment = gstTreatment?.toLowerCase();
  const category = gstCategory?.toLowerCase();
  return treatment === "overseas" || category === "rcm";
}

export function lineItemHasTax(taxId?: string | null): boolean {
  const id = taxId?.trim();
  return !!id && id !== "NO_TAX" && id !== "no_tax";
}

/** Vendor qualifies for RC and at least one line item has a real tax selected. */
export function shouldShowReverseChargeMessage(
  gstTreatment?: string,
  gstCategory?: string,
  lineItems: Array<{ tax_id?: string | null }> = [],
): boolean {
  if (!isReverseChargeVendor(gstTreatment, gstCategory)) {
    return false;
  }
  return lineItems.some((item) => lineItemHasTax(item.tax_id));
}

export type ReverseChargeContext = {
  vendorQualifies: boolean;
  showMessage: boolean;
  taxTotal: number;
  gstTreatment: string;
  gstCategory: string;
  lineItems: Array<{ tax_id?: string | null }>;
  vendorId: string;
};

export function getReverseChargeContextFromJson(
  json: Record<string, unknown> | null | undefined,
  gstTreatment?: string,
  gstCategory?: string,
): ReverseChargeContext {
  const lineItems =
    (json?.line_items as Array<{ tax_id?: string | null }>) ?? [];
  const treatment = String(
    gstTreatment ?? json?.gst_treatment ?? "",
  ).trim();
  const category = String(gstCategory ?? json?.gst_category ?? "").trim();
  const vendorQualifies = isReverseChargeVendor(treatment, category);
  const hasTaxedLine = lineItems.some((li) => lineItemHasTax(li.tax_id));

  return {
    vendorQualifies,
    showMessage: vendorQualifies && hasTaxedLine,
    taxTotal: Number(json?.tax_total) || 0,
    gstTreatment: treatment,
    gstCategory: category,
    lineItems,
    vendorId: String(json?.vendor_id ?? "").trim(),
  };
}

export function formatReverseChargeAmount(
  amount: number,
  currencySymbol = "₹",
): string {
  return `${currencySymbol}${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export type ReverseChargeAmountDisplay = {
  /** Reverse-charge tax in the company base currency. */
  companyAmount: number;
  companySymbol: string;
  /** Reverse-charge tax as entered on the document. */
  documentAmount: number;
  documentSymbol: string;
  /** Document currency is non-base and a usable rate converted the amount. */
  converted: boolean;
};

/**
 * Reverse charge is a GST liability the company self-assesses, so it is owed in
 * the base currency even when the bill is raised in another one. Mirrors
 * BillReverseCharge::taxAmountToInr on the backend: convert only for a real
 * non-base currency with a positive rate, else pass the amount through.
 */
export function resolveReverseChargeAmount(params: {
  taxTotal: number;
  documentCurrencyCode?: string | null;
  documentCurrencySymbol?: string | null;
  exchangeRate?: number | string | null;
  companyCurrencyCode?: string;
}): ReverseChargeAmountDisplay {
  const documentAmount = Number(params.taxTotal) || 0;
  const documentCode = String(params.documentCurrencyCode ?? "")
    .trim()
    .toUpperCase();
  const companyCode = (
    params.companyCurrencyCode ?? getCompanyCurrencyCodeUpper()
  )
    .trim()
    .toUpperCase();
  const rate = Number(params.exchangeRate ?? 0);
  const documentSymbol =
    String(params.documentCurrencySymbol ?? "").trim() ||
    (documentCode ? getCurrencySymbol(documentCode) : "₹");

  const converted =
    Boolean(companyCode) &&
    Boolean(documentCode) &&
    companyCode !== documentCode &&
    Number.isFinite(rate) &&
    rate > 0;

  if (!converted) {
    return {
      companyAmount: roundMoney(documentAmount),
      companySymbol: documentSymbol,
      documentAmount,
      documentSymbol,
      converted: false,
    };
  }

  return {
    companyAmount: roundMoney(documentAmount * rate),
    companySymbol: getCurrencySymbol(companyCode),
    documentAmount,
    documentSymbol,
    converted: true,
  };
}

/**
 * Base-currency amount first, with the amount the user actually entered kept
 * visible in brackets: "₹1,675.17 ($18.00)". Unconverted docs read as before.
 */
export function formatReverseChargeAmountWithDocument(
  display: ReverseChargeAmountDisplay,
): string {
  const base = formatReverseChargeAmount(
    display.companyAmount,
    display.companySymbol,
  );
  if (!display.converted) {
    return base;
  }
  return `${base} (${formatReverseChargeAmount(
    display.documentAmount,
    display.documentSymbol,
  )})`;
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}
